import { Box, Card, FormField, FormHelp, Heading, Input, Label, TextButton } from '@zillow/constellation-10';
import { useId } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CharacterCountFormHelp, useCharacterCount } from '@zg-rentals/visual-cohesion';
import { get } from 'lodash-es';
import type { Contact } from '../../../../../models/rental-application';
import { validateEmail } from '../../../../../utils/validation';
import { isValidUSPhoneNumber } from '../../../../../utils/formatters';
import { MaskedPhoneInput } from '../../../../Inputs';

export const MAX_CONTACT_FIELD_LENGTH = 255;
export const MAX_CONTACT_NOTES_LENGTH = 1000;

type Props = {
  fieldName: string;
  title: string;
  nameLabel?: string;
  hasRelationship?: boolean;
  hasNotes?: boolean;
  hasBusinessAddress?: boolean;
  onDelete?: () => void;
};

type ContactField = {
  key: keyof Contact;
  label: string;
  isVisible: boolean;
  maxLength?: number;
};

function validatePhone(phone: string | null | undefined) {
  if (!phone) {
    return true;
  }
  return isValidUSPhoneNumber(phone) || 'Enter a valid phone number';
}

function ContactNotesField({ name, label }: { name: string; label: string }) {
  const {
    formState: { errors },
    register,
    watch,
  } = useFormContext();
  const { isFocused, onFocus, onBlur } = useCharacterCount();
  const notesLength = String(watch(name) ?? '').length;
  const error = get(errors, name);
  const { onBlur: onRegisterBlur, ...field } = register(name, {
    maxLength: {
      value: MAX_CONTACT_NOTES_LENGTH,
      message: `${label} must be ${MAX_CONTACT_NOTES_LENGTH.toLocaleString()} characters or fewer`,
    },
  });

  return (
    <FormField
      label={<Label>{label}</Label>}
      control={
        <Input
          {...field}
          onFocus={onFocus}
          onBlur={(event) => {
            onBlur();
            onRegisterBlur(event);
          }}
        />
      }
      description={
        <CharacterCountFormHelp
          currentLength={notesLength}
          maxLength={MAX_CONTACT_NOTES_LENGTH}
          isFocused={isFocused}
          label={label}
        />
      }
      error={notesLength > MAX_CONTACT_NOTES_LENGTH || !!error}
      optional
    />
  );
}

export function ContactCard({
  fieldName,
  title,
  nameLabel = 'Full name',
  hasRelationship = false,
  hasNotes = false,
  hasBusinessAddress = false,
  onDelete,
}: Props) {
  const {
    formState: { errors },
    register,
  } = useFormContext();
  const phoneLabelId = useId();

  function renderControl(key: keyof Contact, name: string, maxLength: number) {
    if (key === 'email') {
      return <Input maxLength={maxLength} type="email" {...register(name, { validate: validateEmail })} />;
    }
    if (key === 'phone') {
      return (
        <Controller
          name={name}
          rules={{ validate: validatePhone }}
          render={({ field }) => <MaskedPhoneInput {...field} aria-labelledby={phoneLabelId} />}
        />
      );
    }
    return <Input maxLength={maxLength} {...register(name)} />;
  }

  const fields: Array<ContactField> = [
    { key: 'name', label: nameLabel, isVisible: true },
    { key: 'relationship', label: 'Relationship', isVisible: hasRelationship },
    { key: 'email', label: 'Email', isVisible: true },
    { key: 'phone', label: 'Phone number', isVisible: true },
    { key: 'notes', label: 'Notes', isVisible: hasNotes },
    { key: 'businessAddress', label: 'Business address', isVisible: hasBusinessAddress },
  ];

  return (
    <Card elevated={false} outlined>
      <Box
        css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'default' }}
      >
        <Heading level={4} css={{ textStyle: 'body-bold', color: 'text.subtle' }}>
          {title}
        </Heading>
        {onDelete && (
          <TextButton onClick={onDelete} type="button" aria-label={`Delete ${title}`}>
            Delete
          </TextButton>
        )}
      </Box>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
        {fields
          .filter(({ isVisible }) => isVisible)
          .map(({ key, label, maxLength = MAX_CONTACT_FIELD_LENGTH }) => {
            const name = `${fieldName}.${key}`;
            if (key === 'notes') {
              return <ContactNotesField key={key} name={name} label={label} />;
            }
            const error = get(errors, name);
            return (
              <FormField
                key={key}
                label={<Label id={key === 'phone' ? phoneLabelId : undefined}>{label}</Label>}
                control={renderControl(key, name, maxLength)}
                description={error?.message ? <FormHelp>{String(error.message)}</FormHelp> : undefined}
                error={!!error}
                optional
              />
            );
          })}
      </Box>
    </Card>
  );
}
